import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { RendererProps } from "../FilePreviewModal";
import CustomScrollbars from "../../CustomScrollbars";
import SearchBar from "../SearchBar";
import { HighlightedText } from "../searchUtils";
import { useDomSearchNavigation } from "../useDomSearchNavigation";

const MAX_PREVIEW_BYTES = 5 * 1024 * 1024;

const TextRenderer: React.FC<RendererProps> = ({ blob }) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState("");
  const contentRef = useRef<HTMLPreElement | null>(null);

  const { total, current, gotoNext, gotoPrev } = useDomSearchNavigation(
    contentRef,
    [text, search],
  );

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        const slice =
          fileBlob.size > MAX_PREVIEW_BYTES
            ? fileBlob.slice(0, MAX_PREVIEW_BYTES)
            : fileBlob;
        const t = await slice.text();
        if (cancelled) return;
        setText(t);
        setTruncated(fileBlob.size > MAX_PREVIEW_BYTES);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to read text");
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (text == null) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          matchCount={total}
          currentMatch={current}
          onPrev={gotoPrev}
          onNext={gotoNext}
        />
      </Box>
      {truncated && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          File is larger than 5 MB — showing only the first part. Download to
          view the full content.
        </Alert>
      )}
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
        <Box
          component="pre"
          ref={contentRef}
          sx={{
            m: 0,
            p: 2,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Liberation Mono", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            backgroundColor: "background.paper",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {search ? <HighlightedText text={text} query={search} /> : text}
        </Box>
      </CustomScrollbars>
    </Box>
  );
};

export default TextRenderer;
